<template>
  <aside class="_sidebar" id="sidePanel">

    <!-- Sidebar Toggle -->
    <SidebarToggle
      :items="quickInfoItems"
      :isSidebarOpen.sync="isSidebarOpen"
    />

    <!-- Sidebar Tabs -->
    <div class="_sidebar__body" id="sidePanelBody">
      <SidebarTabs :tabs="tabs" :activeTabId="activeTabId" />
    </div>

  </aside>
</template>

<script>
import _ from 'lodash';
import SidebarTabs from '@/components/Sidebar/SidebarTabs';
import SidebarToggle from '@/components/Sidebar/SidebarToggle';

export default {
  name: 'side-panel',
  components: {
    SidebarTabs,
    SidebarToggle,
  },
  props: {
    toggleBarInfoItems: {
      type: Array,
      required: false,
    }
  },
  data: () => ({
    isSidebarOpen: false,
    tabs: [],
    activeTabId: '',
  }),
  computed: {
    /**
     * isEnabled is a computed prop obtained from route meta config
     * @return {Mixed} Boolean or Undefined
     */
    isEnabled() {
      return this.$reduce(this, '$route.meta.sidebar.isEnabled');
    },
    /**
     * Get sidebar quick info items
     * @return {Array} an array ob objects each wih id, icon and label attribute
     */
    quickInfoItems() {
      const items = this.$reduce(this, '$route.meta.sidebar.quickInfoItems');
      if (!items || !items.length) {
        return [];
      }
      return items;
    },
    urlHash() {
      return this.$reduce(this, '$route.hash');
    }
  },
  mounted() {
    this.initTabs();
    window.$bus.$on('sidepanel.open', () => this.toggleSidepanel(true));
    window.$bus.$on('sidepanel.close', () => this.toggleSidepanel(false));
    window.$bus.$on('sidepanel.toggle', () => this.toggleSidepanel(!this.isSidebarOpen));
    window.$bus.$on('sidepanel.tabs.change', (tabId) => this.setActiveTabId(tabId));
    window.$bus.$on('sidepanel.tabs.toggle', (tabId, isDisabled) => this.toggleTab(tabId, isDisabled));
  },
  beforeDestroy() {
    this.onSidebarDestroy();
  },
  methods: {
    /**
     * Perform tasks when sidebar is mounted
     * @returns {void}
     */
    onSidebarMount() {
      // add CSS class to body - this will help in styling elements
      document.body.classList.add('_sidebarIsActive');
    },
    /**
     * Perform tasks when sidebar is destroyed
     * @returns {void}
     */
    onSidebarDestroy() {
      // reove CSS class from body added during mount
      document.body.classList.remove('_sidebarIsActive');
      document.body.classList.remove('_sidebarIsOn');
      // this is being added by swal :(
      document.body.style.removeProperty('padding-right');
      this.isSidebarOpen = false;
    },
    /**
     * If deep link found i.e. URL contains a hash then open sidebar
     * @return {void}
     */
    openSidebarIfHashFound() {
      const hash = this.$reduce(this, '$route.hash');
      if (!hash) {
        return;
      }
      this.isSidebarOpen = true;
    },
    initTabs() {
      let tabs = this.$reduce(this, '$route.meta.sidebar.tabs');
      if (!tabs || !tabs.length) {
        tabs = []
      }
      this.tabs = _.cloneDeep(tabs);
      this.isSidebarOpen = this.$reduce(this, '$route.meta.sidebar.isOpen');
    },
    /**
     * Toggle sidebar visiblity
     * @param  {Boolean} isSidebarOpen whether to open or close the sidebar
     * @return {void}
     */
    toggleSidepanel(isSidebarOpen) {
      this.isSidebarOpen = isSidebarOpen ? true : false;
    },
    /**
     * Change active tab - use setImmediate to force change detection
     * @param  {String} tabId new active tab id
     * @return {void}
     */
    setActiveTabId(tabId) {
      this.activeTabId = null;
      setImmediate(() => {
        this.activeTabId = tabId;
      });
    },
    /**
     * Enable a tab
     * @param  {String} tabId tab id
     * @return {void}
     */
    enableTab(tabId) {
      const tab = this.tabs.find(_tab => tabId == _tab.id);
      if (!tab) {
        return;
      }
      this.$set(tab, 'isDisabled', false);
    },
    /**
     * Disable a tab
     * @param  {String} tabId tab id
     * @return {void}
     */
    disableTab(tabId) {
      const tab = this.tabs.find(_tab => tabId == _tab.id);
      if (!tab) {
        return;
      }
      this.$set(tab, 'isDisabled', true);
    },
    /**
     * Enable or disable a tab
     * @param  {String} tabId tab id
     * @param  {Boolean} isDisabled is tab disabled?
     * @return {void}
     */
    toggleTab(tabId, isDisabled) {
      if (isDisabled) {
        return this.disableTab(tabId);
      }
      this.enableTab(tabId);
    },
    scrollToElement() {
      let parentEle = document.getElementById('sidePanelBody');
      let id = this.$route.hash.replace("#", "");
      const targetEle = document.getElementById(id);

      if (!parentEle || !targetEle) {
        return
      }
      
      /**
       * ATLAS-1142: <Service-Sidepanel><Deeplink> Deeplink is not working, 
       * Upon clicking it, it is not auto-scrolling to the proper Reply message.
       * @see https://zeamster.atlassian.net/browse/ATLAS-1142
       * Don't scroll if it is the last link in hisotry
       */
      const url = `${window.location.origin}/#${this.$route.fullPath}`
      if (url == this.$Session.deepLinkHistory[0]) {
        return
      }
      this.$Session.deepLinkHistory.unshift(url)
      
      // if (targetEle) {
      //   parentEle.scrollTo({
      //     top: this.getOffset(targetEle),
      //     left: 0,
      //   });
      // }
      console.log('Scrolling to target element from Sidebar.vue')
      targetEle.scrollIntoView(true)
    },
    getOffset(targetEle) {
      if (!targetEle.getBoundingClientRect) {
        return 0;
      }
      const headerEle = document.querySelector('.md-toolbar');
      let offset = targetEle.getBoundingClientRect().top
      if (headerEle) {
        offset -= headerEle.getBoundingClientRect().height;
      }
      return offset;
    }
  },
  watch: {
    /**
     * Show or hide the side panel depending on the routes
     */
    'isEnabled': {
      immediate: true,
      handler: function(newVal, oldVal) {
        if (this.isEnabled) {
          this.onSidebarMount();
        } else {
          this.onSidebarDestroy();
        }
      }
    },
    'urlHash': {
      immediate: true,
      handler: function(newVal, oldVal) {
        if (this.isEnabled) {
          this.openSidebarIfHashFound();
          this.scrollToElement();
          // setTimeout(() => {
          //   this.scrollToElement();
          // }, 3000);
        }
      }
    },
    '$route.params.id': {
      immediate: true,
      handler() {
        this.initTabs();
      }
    },
    '$route.meta.sidebar': {
      immediate: true,
      handler() {
        this.initTabs();
      }
    },
    '$route.meta.sidebar.activeTabId': {
      immediate: true,
      handler() {
        this.setActiveTabId(this.$reduce(this, '$route.meta.sidebar.activeTabId'));
      }
    }
  }
}
</script>

<style lang="scss">
// 
</style>
